import React, { forwardRef } from 'react';
import { useHistory } from 'react-router-dom';

import {
    Kpis,
    LeftNavContentSection,
    MediaExplorer,
    StatementEarnings,
    StatementRanges,
} from '@trqk/components';
import { useIntersectionObserver } from '@trqk/hooks';
import { removeNulls } from '@trqk/utils';

import { EarningRegions, EarningsBreakdown } from '..';
import { sectionMap, sections } from '../../constants';
import { MAIN_CONTENT_ID } from '../../../../../constants';
import { getFeatureFlags } from '../../../../../featureFlags';

const chartPanelHeight = 520;
const paddingOffset = 48; // 2 * p-6, or 2 * (1.5rem * 16px base rem unit)
const headerOffset = 20; // text-xl, or 1.25rem * 16px base rem unit
const widePanelHeight = chartPanelHeight - paddingOffset - headerOffset;
const sectionIds = sections.map(({ id }: HeaderSection): string => id);

interface StatementsChartsContentProps {
    /**
     * A unique ID for the dashboard. For StatementChartsPreview, this will be the statement_id;
     * for StatementCharts, it will be the merged statement id.
     */
    id: string;
    /**
     * The dashboard to render
     */
    dashboard: ProIngestionDashboard;
    /**
     * Whether the media explorer data is being loaded
     */
    loadingMediaExplorerData: boolean;
    /**
     * A tree of {@link ProDirectedTreeNode} nodes
     */
    mediaExplorerData: ProDirectedTreeNode[] | null;
    /**
     * Whether to show the Media Explorer (i.e. directed tree chart)
     */
    showMediaExplorer: boolean;
}

/**
 * Displays a visualization of one or more PRO statements, as a series of charts.
 */
export const StatementsChartsContent = forwardRef(
    (
        {
            dashboard,
            id,
            loadingMediaExplorerData,
            mediaExplorerData,
            showMediaExplorer,
        }: StatementsChartsContentProps,
        ref,
    ): JSX.Element => {
        const { truncateChartData } = getFeatureFlags();
        const kpis = dashboard?.kpis ?? null;
        const statementInfo = dashboard?.statement_info ?? null;
        const shows: ProShow[] = removeNulls(dashboard.shows);
        const countryCodes: ProCountryCode[] = removeNulls(
            dashboard.country_codes,
        );
        const history = useHistory();

        useIntersectionObserver({
            defaultSectionId: sections[0].id,
            sectionIds,
            observerOptions: {
                root: document.querySelector(MAIN_CONTENT_ID),
                rootMargin: '0px',
                threshold: 0.8,
            },
            onSetActiveSectionId: (activeSectionId: string) => {
                if (history.location.hash !== activeSectionId) {
                    history.replace({
                        hash: activeSectionId,
                        search: history.location.search,
                    });
                }
            },
        });

        return (
            <div className="flex flex-col w-full">
                <div className="mb-10">
                    <LeftNavContentSection
                        id={
                            showMediaExplorer
                                ? sectionMap.explorer.id
                                : sectionMap.earningsAndRanges.id
                        }
                        ref={ref}
                    >
                        <Kpis kpis={kpis} statementInfo={statementInfo} />
                    </LeftNavContentSection>
                    {showMediaExplorer && (
                        <LeftNavContentSection
                            id={sectionMap.explorer.id}
                            ref={ref}
                        >
                            <MediaExplorer
                                id={id}
                                data={mediaExplorerData}
                                loading={loadingMediaExplorerData}
                            />
                        </LeftNavContentSection>
                    )}
                    <LeftNavContentSection
                        id={sectionMap.earningsAndRanges.id}
                        ref={ref}
                    >
                        <div className="flex">
                            <div className="w-1/2 mr-3 bg-white rounded-lg">
                                <StatementEarnings
                                    id={id}
                                    dashboard={dashboard}
                                    containerHeight={chartPanelHeight}
                                    truncateChartData={truncateChartData}
                                />
                            </div>
                            <div className="w-1/2 ml-3 bg-white rounded-lg">
                                <StatementRanges
                                    id={id}
                                    dashboard={dashboard}
                                    containerHeight={chartPanelHeight}
                                    truncateChartData={truncateChartData}
                                />
                            </div>
                        </div>
                    </LeftNavContentSection>
                    <LeftNavContentSection
                        id={sectionMap.breakdown.id}
                        ref={ref}
                    >
                        <EarningsBreakdown
                            id={id}
                            height={widePanelHeight}
                            shows={shows}
                            truncateChartData={truncateChartData}
                        />
                    </LeftNavContentSection>
                    <LeftNavContentSection id={sectionMap.regions.id} ref={ref}>
                        <EarningRegions
                            id={id}
                            countryCodes={countryCodes}
                            height={widePanelHeight}
                        />
                    </LeftNavContentSection>
                </div>
            </div>
        );
    },
);

StatementsChartsContent.displayName = 'StatementsChartsContent';
